/**
 * Lädt den Datenbestand und prüft ihn beim Laden gegen die Schemata.
 * Ungültige Daten sollen sofort auffallen, nicht erst in der Szene.
 */
import vesselsRaw from '../../data/vessels.json'
import lightStatesRaw from '../../data/light-states.json'
import binnenRaw from '../../data/binnen-lichter.json'
import buoysRaw from '../../data/buoys.json'
import signalsRaw from '../../data/signals.json'
import binnenSignaleRaw from '../../data/binnen-schallzeichen.json'
import scenariosRaw from '../../data/scenarios.json'
import questionsRaw from '../../data/questions.json'
import type { ZodType } from 'zod'
import {
  binnenLightStatesFile,
  binnenSignalsFile,
  buoysFile,
  lightStatesFile,
  questionsFile,
  scenariosFile,
  signalsFile,
  vesselsFile,
  type BinnenLightStateData,
  type BinnenSignalData,
  type BuoyData,
  type LightStateData,
  type QuestionData,
  type ScenarioData,
  type SignalData,
  type VesselTypeData,
} from './schema'

/**
 * Beim Laden prüfen, aber mit einer Meldung, mit der man etwas anfangen kann: der
 * Fehlerbericht von zod nennt den Pfad in der Datei, und genau der gehört in die erste
 * Zeile, nicht in einen Stapel aus Stacktrace.
 */
function load<T>(file: string, schema: ZodType<T>, raw: unknown): T {
  const result = schema.safeParse(raw)
  if (result.success) return result.data
  const issue = result.error.issues[0]
  const where = issue?.path.length ? ` bei ${issue.path.join('.')}` : ''
  throw new Error(`${file}${where}: ${issue?.message ?? 'passt nicht zum Schema'}`)
}

export const vessels: VesselTypeData[] = load('vessels.json', vesselsFile, vesselsRaw)
export const lightStates: LightStateData[] = load('light-states.json', lightStatesFile, lightStatesRaw)
export const binnenStates: BinnenLightStateData[] = load(
  'binnen-lichter.json',
  binnenLightStatesFile,
  binnenRaw,
)
export const buoys: BuoyData[] = load('buoys.json', buoysFile, buoysRaw)
export const signals: SignalData[] = load('signals.json', signalsFile, signalsRaw)
/**
 * Die Schallzeichen der Binnenschifffahrt, Anlage 6 der Ordnung. Eigener Bestand neben
 * den Seesignalen: gleiche Töne heißen dort Verschiedenes, und eine gemeinsame Liste
 * hätte genau das verwischt.
 */
export const binnenSignale: BinnenSignalData[] = load(
  'binnen-schallzeichen.json',
  binnenSignalsFile,
  binnenSignaleRaw,
)
export const scenarios: ScenarioData[] = load('scenarios.json', scenariosFile, scenariosRaw)
export const questions: QuestionData[] = load('questions.json', questionsFile, questionsRaw)

const vesselById = new Map(vessels.map((v) => [v.id, v]))
const stateById = new Map(lightStates.map((s) => [s.id, s]))
const buoyById = new Map(buoys.map((b) => [b.id, b]))
const scenarioById = new Map(scenarios.map((s) => [s.id, s]))

export function vessel(id: string): VesselTypeData {
  const v = vesselById.get(id)
  if (!v) throw new Error(`Fahrzeugtyp "${id}" gibt es nicht`)
  return v
}

export function lightState(id: string): LightStateData {
  const s = stateById.get(id)
  if (!s) throw new Error(`Lichterzustand "${id}" gibt es nicht`)
  return s
}

export function buoy(id: string): BuoyData {
  const b = buoyById.get(id)
  if (!b) throw new Error(`Seezeichen "${id}" gibt es nicht`)
  return b
}

/** Lichterzustände nach Regelnummer gruppiert, Reihenfolge wie im Regelwerk. */
export function scenario(id: string): ScenarioData {
  const s = scenarioById.get(id)
  if (!s) throw new Error(`Ausweichszene "${id}" gibt es nicht`)
  return s
}

export function statesByRule(): Map<number, LightStateData[]> {
  const out = new Map<number, LightStateData[]>()
  for (const s of [...lightStates].sort((a, b) => a.rule - b.rule)) {
    const list = out.get(s.rule) ?? []
    list.push(s)
    out.set(s.rule, list)
  }
  return out
}

const binnenById = new Map(binnenStates.map((s) => [s.id, s]))

/**
 * Ein Binnenzustand, oder ein Fehler mit Namen.
 *
 * See und Binnen liegen bewusst in getrennten Beständen und in getrennten Nachschlagen:
 * die Ordnungen widersprechen sich in der Sache (ein Topplicht statt zweier, blaue Lichter
 * für gefährliche Güter, eigene Bezeichnung für Verbände), und ein gemeinsamer Bestand
 * wäre eine Einladung, das zu verwischen.
 */
export function binnenState(id: string): BinnenLightStateData {
  const s = binnenById.get(id)
  if (!s) throw new Error(`Binnenzustand "${id}" gibt es nicht`)
  return s
}

/** Gehört die Kennung zum Binnenbestand? */
export function istBinnen(id: string): boolean {
  return binnenById.has(id)
}

/** Binnenzustände nach Paragraph gruppiert, in der Reihenfolge der Ordnung. */
export function binnenNachParagraph(): Map<string, BinnenLightStateData[]> {
  const out = new Map<string, BinnenLightStateData[]>()
  const sortiert = [...binnenStates].sort((a, b) => a.paragraph.localeCompare(b.paragraph, 'de'))
  for (const s of sortiert) {
    const list = out.get(s.paragraph) ?? []
    list.push(s)
    out.set(s.paragraph, list)
  }
  return out
}
